import type { Branch } from "../providers/branchesProvider";
import {
  BooleanInput,
  Create,
  Edit,
  NumberInput,
  required,
  Resource,
  TextInput,
  useGetManyReference,
  useGetOne,
  useRedirect,
} from "react-admin";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { DrivingSchool } from "../providers/drivingSchoolsProvider";
import { Instructor } from "../providers/instructorsProvider";
import { Column } from "../misc/Column";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { Form2 } from "../misc/Form2";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Row } from "../misc/Row";
import { grants } from "../backoffice.access_control";
import { SaveButton2 } from "../buttons/SaveButton2";
import { DefaultList } from "../misc/DefaultList";
import { SelectInput } from "../inputs/SelectInput";
import { useCurrentDrivingSchoolId } from "../hooks/useCurrentDrivingSchoolId";
import { RecordDetailsHeader } from "../misc/RecordDetailsHeader";

function CreateBranchForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();
  const { data: drivingSchool } = useGetOne<DrivingSchool>(
    "drivingSchools",
    { id: drivingSchoolId! },
    { enabled: !!drivingSchoolId },
  );

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  if (!drivingSchool) {
    return <LoadingIndicator />;
  }

  return (
    <Create
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Filiale erfolgreich erstellt.");
          redirect(`/drivingSchools/${drivingSchoolId}/branches`);
        },
        onError: (error: unknown) => {
          handleError("Fehler beim Erstellen der Filiale.", error);
        },
      }}
      transform={(data: Branch) => ({
        ...data,
        drivingSchoolId,
        address: {
          ...data.address,
          company: drivingSchool.name,
        },
      })}
    >
      <BranchForm mode="create" />
    </Create>
  );
}

function EditBranchForm() {
  const redirect = useRedirect();
  const branch = useRecordAndSwitchDrivingSchoolIfNecessary<Branch>((it) => it.drivingSchoolId);

  if (!branch) {
    return <LoadingIndicator />;
  }

  return (
    <Edit
      sx={branch.isActive ? {} : { "& .MuiPaper-root": { backgroundColor: "action.selected" } }}
      title={
        <BackofficeRecordTitle
          header={
            <RecordDetailsHeader
              backLabel={branch.isActive ? "Filialen" : "Geschlossene Filialen"}
              backUrl={`/drivingSchools/${branch.drivingSchoolId}/branches${branch.isActive ? "" : "#inactive"}`}
              title={branch.name}
            />
          }
        />
      }
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert.");
          redirect(`/drivingSchools/${branch.drivingSchoolId}/branches`);
        },
        onError: (error: unknown) => {
          handleError("Fehler beim Speichern.", error);
        },
      }}
    >
      <BranchForm mode={branch.isActive ? "edit" : "show"} />
    </Edit>
  );
}

function BranchForm(props: { mode: "create" | "edit" | "show" }) {
  const { mode } = props;
  const readOnly = mode === "show" || !grants.has("Branch:edit");
  return (
    <Form2 warnWhenUnsavedChanges>
      <Column sx={{ padding: overviewCardStyle.padding }}>
        <TextInput label="Name" source="name" autoFocus={mode === "create"} validate={required()} readOnly={readOnly} />
        <NumberInput
          label="Max Schüleranzahl pro Theoriestunde"
          source="maxStudentsForTheory"
          validate={required()}
          readOnly={readOnly}
        />
        <DefaultDrivingSchoolManagerInput readOnly={readOnly} />
        <BooleanInput label="Haupt-Filiale" source="isDefaultBranch" readOnly={readOnly} />

        <Divider sx={{ width: "100%", my: 2 }} />

        <FormGroup sx={{ width: "100%" }}>
          <FormLabel sx={{ mb: 1, fontWeight: "bold" }}>Filialadresse</FormLabel>
          <TextInput label="Straße" source="address.street" validate={required()} readOnly={readOnly} />
          <TextInput label="PLZ" source="address.postalCode" validate={required()} readOnly={readOnly} />
          <TextInput label="Ort" source="address.city" validate={required()} readOnly={readOnly} />
        </FormGroup>

        <Divider sx={{ width: "100%", my: 2 }} />

        <FormGroup sx={{ width: "100%" }}>
          <FormLabel sx={{ mb: 1, fontWeight: "bold" }}>Adresse für Verträge (optional)</FormLabel>
          <TextInput label="Firma" source="contractAddress.company" readOnly={readOnly} />
          <TextInput label="Straße" source="contractAddress.street" readOnly={readOnly} />
          <TextInput label="PLZ" source="contractAddress.postalCode" readOnly={readOnly} />
          <TextInput label="Ort" source="contractAddress.city" readOnly={readOnly} />
        </FormGroup>

        <Divider sx={{ width: "100%", my: 2 }} />

        <FormGroup sx={{ width: "100%" }}>
          <FormLabel sx={{ mb: 1, fontWeight: "bold" }}>Rechnungsadresse (optional)</FormLabel>
          <TextInput label="Firma" source="invoiceAddress.company" readOnly={readOnly} />
          <TextInput label="Straße" source="invoiceAddress.street" readOnly={readOnly} />
          <TextInput label="PLZ" source="invoiceAddress.postalCode" readOnly={readOnly} />
          <TextInput label="Ort" source="invoiceAddress.city" readOnly={readOnly} />
        </FormGroup>

        {!readOnly && (
          <Row sx={{ pt: 1, justifyContent: "space-between" }}>
            <SaveButton2 />
          </Row>
        )}
      </Column>
    </Form2>
  );
}

export const branchesResource = (
  <Resource
    key="branches"
    name="branches"
    list={DefaultList}
    create={CreateBranchForm}
    edit={EditBranchForm}
    options={{ label: "Filialen", createTitle: "Neue Filiale" }}
    recordRepresentation={(branch: Branch) => branch.name}
  />
);

function DefaultDrivingSchoolManagerInput(props: { readOnly: boolean }) {
  const { readOnly } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const { data: instructors, isLoading } = useGetManyReference<Instructor>("instructors", {
    target: "drivingSchoolId",
    id: drivingSchoolId,
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 999 },
    filter: { isActive: true, isDrivingSchoolManager: true },
  });
  if (isLoading) {
    return null;
  }
  const options: Array<[string, string]> = [
    ["", ""],
    ...(instructors ?? []).map((instructor): [string, string] => [instructor.id, instructor.name]),
  ];
  return (
    <SelectInput
      source="defaultDrivingSchoolManagerId"
      label="Standard-Fahrschulleiter"
      options={options}
      disabled={readOnly}
      size="small"
      sx={{ mb: 2.5 }}
    />
  );
}
